import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { compact, isEmpty, size } from 'lodash';
import cn from 'classnames';
import {
  faBooks,
  faExternalLinkSquare,
} from '@fortawesome/pro-regular-svg-icons';
import { Button, createBemBlockBuilder, Hyperlink } from '@ebsco-ui/ebsco-ui';

import { Dropdown } from '@app/components';
import { useRecordSummaryContext } from '@app/contexts';
import { useFeatureFlag, useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { openUrlInNewTab, processElectronicAccess } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { Feature, ShapeWithClassName } from '@app/constants';

import './AccessOptions.scss';

const cnBem = createBemBlockBuilder(['accessOptions']);

export const AccessOptions: FC<ShapeWithClassName> = ({ className }) => {
  const {
    availability = { isRequestable: true },
    links,
    instance,
    isAvailabilityLoadingForTheFirstTime,
    LCLinkResolverLink,
    toggleIsPlaceHoldModalOpen,
  } = useRecordSummaryContext();
  const { selectConfiguredOption } = useHoldsAndCheckoutsPageConfig();
  const { isRequestable } = availability;
  const { $t } = useIntl();
  const placeHoldText = selectConfiguredOption(
    $t(sharedMessages.requestItem),
    $t(sharedMessages.placeHold)
  );
  const { isActive: isPlaceHoldFlagActive } = useFeatureFlag({
    name: Feature.placeHold,
    isActiveIfNotFound: true,
  });
  const accessLinks = useMemo(
    () => processElectronicAccess(instance.electronicAccess),
    [instance.electronicAccess]
  );
  const hasPlaceHold = isRequestable && isPlaceHoldFlagActive;

  const handleDropdownChange = useCallback(
    ({ selectedItem: { value } }) => {
      const isPlaceHold = value === 'placeHold';

      if (isPlaceHold) {
        toggleIsPlaceHoldModalOpen();

        return;
      }

      if (value) {
        openUrlInNewTab(value);
      }
    },
    [toggleIsPlaceHoldModalOpen]
  );

  const dropdownItems = useMemo(
    () =>
      compact([
        hasPlaceHold && {
          label: placeHoldText,
          disabled: isAvailabilityLoadingForTheFirstTime,
          icon: faBooks,
          value: 'placeHold',
        },
        ...accessLinks.map(link => ({
          label: link.linkText,
          icon: faExternalLinkSquare,
          value: link.uri,
        })),
      ]).map((item, index) => ({ ...item, index })),
    [
      hasPlaceHold,
      placeHoldText,
      isAvailabilityLoadingForTheFirstTime,
      accessLinks,
    ]
  );

  const renderAccessButton = () => {
    if (isEmpty(dropdownItems)) {
      return null;
    }

    if (size(dropdownItems) > 1) {
      return (
        <div className={cnBem('__dropdown')}>
          <Dropdown
            id="access-options-dropdown"
            placeholder={$t({
              id: 'resultsList.accessOptions',
              defaultMessage: 'Access options',
            })}
            items={dropdownItems}
            onSelectedItemChange={handleDropdownChange}
          />
        </div>
      );
    }

    if (hasPlaceHold) {
      return (
        <Button
          disabled={isAvailabilityLoadingForTheFirstTime}
          onClick={toggleIsPlaceHoldModalOpen}
        >
          {placeHoldText}
        </Button>
      );
    }

    const { uri, linkText } = accessLinks[0];

    return (
      <Hyperlink
        className={cnBem('__electronicAccessLink')}
        styleType="primary"
        href={uri}
        target="_blank"
      >
        <span>{linkText}</span>
      </Hyperlink>
    );
  };

  const handleLCLinkResolverButtonClick = () => {
    window.open(LCLinkResolverLink, '_blank');
  };

  return (
    <div className={cn(cnBem(), className)}>
      {renderAccessButton()}
      {LCLinkResolverLink && (
        <Button onClick={handleLCLinkResolverButtonClick}>
          {$t({
            id: 'resultsList.LCFindIt',
            defaultMessage: 'LC find it',
          })}
        </Button>
      )}
      {links}
    </div>
  );
};
